import Image from 'next/image';
import Link from 'next/link';
import type { MouseEvent, ReactNode } from 'react';
import { cn, currencyFormatter, numberFormatter } from '@/lib/utils';
import { ChangeText } from '../ChangeText';
import { useTranslation } from '@/lib/i18n';
import { useFollowing } from '@/hooks/useFollowing';
import BookmarkButton from '@/components/predic/ui/BookmarkButton';
import { trackXAdsEvent } from '@/lib/xAds';

export type MarketCardVariant = 'base' | 'wide' | 'compact';

export interface MarketCardMarket {
  marketEventId?: string;
  imageSrc: string;
  imageAlt?: string;
  title: string;
  outcome: string;
  probability: number;
  change: number;
  changePeriod: string;
  volume: number;
  category: string;
  statusLabel?: string;
  resolvedOutcome?: string;
}

export interface MarketCardProps {
  marketEventId: string;
  market: MarketCardMarket;
  variant?: MarketCardVariant;
  className?: string;
  onBookmarkClick?: () => void | Promise<void>;
  bookmarkLoading?: boolean;
  showBookmark?: boolean;
  href?: string;
  linkEnabled?: boolean;
  followed?: boolean;
}

function formatProbability(value: number) {
  return `${numberFormatter(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

function MarketBadge({
  children,
  muted,
  className,
}: {
  children: ReactNode;
  muted?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'flex h-4 items-center rounded-sm bg-[#323131] px-1.75 text-[14px] leading-4 text-white capitalize',
        muted && 'text-[#B7B9BC]',
        className,
      )}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function Divider({ className }: { className?: string }) {
  return <span className={cn('h-3 w-px shrink-0 bg-[#3A3A3A]', className)} aria-hidden="true" />;
}

function ActiveStats({ market, isCompact }: { market: MarketCardMarket; isCompact?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 gap-3 flex-col">
      <div className="flex min-w-0 items-center gap-3">
        <MarketBadge>
          {market.outcome.length > 12 ? `${market.outcome.slice(0, 12)}...` : market.outcome}
        </MarketBadge>
        <span className="shrink-0 text-sm leading-4 text-white">
          {formatProbability(market.probability)}
        </span>
        <span className="flex h-4 shrink-0 items-center gap-1.25">
          <ChangeText value={market.change} />
          <span className="text-xs leading-4 tracking-[-0.15px] text-[#757575]">
            {t(market.changePeriod)}
          </span>
        </span>
      </div>
      <div className="flex min-w-0 items-center gap-3">
        {market.statusLabel && (
          <>
            <MarketBadge className={cn(market.statusLabel !== 'Ended' && 'bg-amber-500')}>
              {t(market.statusLabel)}
            </MarketBadge>
            <Divider />
          </>
        )}
        {!isCompact && (
          <>
            <span className="shrink-0 text-sm leading-4 text-white/40 capitalize">
              {market.category}
            </span>
            <Divider />
          </>
        )}
        <span className="shrink-0 text-sm leading-4 text-white/40">
          {t('{{volume}} Vol ({{period}})', {
            volume: currencyFormatter(market.volume),
            period: t('24h'),
          })}
        </span>
      </div>
    </div>
  );
}

function StatusStats({ market }: { market: MarketCardMarket }) {
  const label = market.statusLabel ?? 'Ended';
  const { t } = useTranslation();
  const outcome =
    market.outcome.toLowerCase() === market.resolvedOutcome?.toLowerCase()
      ? market.outcome
      : `${market.outcome} ${market.resolvedOutcome || ''}`;

  return (
    <div className="flex items-center gap-3 @max-sm:gap-2">
      <MarketBadge muted>{t(label)}</MarketBadge>
      <MarketBadge muted>{outcome}</MarketBadge>
      {/* {market.resolvedOutcome && <MarketBadge muted>{market.resolvedOutcome}</MarketBadge>} */}
      <Divider />
      <span className="shrink-0 text-sm leading-4 text-white/40">{market.category}</span>
      <Divider />
      <span className="shrink-0 text-sm leading-4 text-white/40">
        {t('{{volume}} Vol ({{period}})', {
          volume: currencyFormatter(market.volume),
          period: t('24h'),
        })}
      </span>
    </div>
  );
}

export function MarketCard({
  marketEventId,
  market,
  variant = 'base',
  showBookmark = true,
  className,
  onBookmarkClick,
  bookmarkLoading,
  followed,
  href,
  linkEnabled = true,
}: MarketCardProps) {
  const resolvedMarketEventId = marketEventId || market.marketEventId || '';
  const isInactive = market.statusLabel === 'Ended';
  const resolvedVariant = isInactive ? 'base' : variant;
  const isCompact = resolvedVariant === 'compact';
  const canShowBookmark = showBookmark && !isCompact;
  const { isFollowed, isFollowing, toggleFollow } = useFollowing(
    canShowBookmark ? resolvedMarketEventId : null,
    followed,
  );
  const bookmarkActive = resolvedMarketEventId ? isFollowed : false;
  const isBookmarkLoading = bookmarkLoading ?? isFollowing;
  const imageClassName = cn(
    'size-11 shrink-0 rounded-lg object-cover m-0!',
    isInactive && 'grayscale',
  );

  async function handleBookmarkClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (onBookmarkClick) {
      await onBookmarkClick();
      return;
    }

    await toggleFollow();
  }

  const content = (
    <article
      className={cn(
        'flex w-full flex-col gap-3 rounded-xl border border-[#1E1E1E] bg-[#121212] p-4 text-white',
        isCompact && 'border-white/15 bg-black/55 p-3',
        className,
      )}
      onClick={() => {
        trackXAdsEvent('click_event_card', {
          market_id: marketEventId,
          market_title: market.title,
        });
      }}
    >
      {isCompact ? (
        <div className="flex min-w-0 items-center gap-3">
          <Image
            src={market.imageSrc}
            alt={market.imageAlt ?? ''}
            width={44}
            height={44}
            className={imageClassName}
            // style={{
            //   background:
            //     'linear-gradient(110deg,#0000,#ffffffd9 50%,#0000),linear-gradient(#cd9d00 0%,#997500 100%)',
            // }}
          />
          <div className="min-w-0 flex-1">
            <h2 className="truncate leading-5.5 text-white">{market.title}</h2>
            <div className="mt-1">
              <ActiveStats market={market} isCompact />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <Image
              src={market.imageSrc}
              alt={market.imageAlt ?? ''}
              width={44}
              height={44}
              className={imageClassName}
              // style={{
              //   background:
              //     'linear-gradient(110deg,#0000,#ffffffd9 50%,#0000),linear-gradient(#cd9d00 0%,#997500 100%)',
              // }}
            />

            <h2
              className={cn(
                'flex-1 font-medium text-white line-clamp-2 text-base! m-0! leading-5.5',
                isInactive && 'text-[#B7B9BC]',
              )}
            >
              {market.title}
            </h2>

            {canShowBookmark && (
              <BookmarkButton
                isFollowed={bookmarkActive}
                isFollowing={isBookmarkLoading}
                onClick={handleBookmarkClick}
              />
            )}
          </div>

          <div className={cn('flex items-center @container')}>
            {isInactive ? <StatusStats market={market} /> : <ActiveStats market={market} />}
          </div>
        </>
      )}
    </article>
  );

  if (href && linkEnabled) {
    return (
      <Link href={href} className="block">
        {content}
      </Link>
    );
  }

  return content;
}

export default MarketCard;
